import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { AuthProvider, useAuth } from './context/AuthContext';
import { LicenciaProvider } from './context/LicenciaContext';
import { ThemeProvider } from './context/ThemeContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Clientes from './pages/Clientes';
import Creditos from './pages/Creditos';
import Rutas from './pages/Rutas';
import Cobros from './pages/Cobros';
import Reportes from './pages/Reportes';
import Usuarios from './pages/Usuarios';
import GuiaRuta from './pages/GuiaRuta';
import Configuracion from './pages/Configuracion';
import ClienteDetalle from './pages/ClienteDetalle';
import Productos from './pages/Productos';
import Ventas from './pages/Ventas';
import NuevaVenta from './pages/NuevaVenta';

// Admin SaaS
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminClientes from './pages/admin/AdminClientes';
import AdminClienteDetalle from './pages/admin/AdminClienteDetalle';
import AdminPlanes from './pages/admin/AdminPlanes';
import AdminSuscripciones from './pages/admin/AdminSuscripciones';
import AdminPagos from './pages/admin/AdminPagos';
import AdminCRM from './pages/admin/AdminCRM';
import AdminReportes from './pages/admin/AdminReportes';
import LicenciaGuard from './components/LicenciaGuard';

function PrivateRoute({ children, soloAdmin }) {
  const { usuario } = useAuth();
  if (!usuario) return <Navigate to="/login" />;
  if (soloAdmin && usuario.rol !== 'admin') return <Navigate to="/dashboard" />;
  return (
    <LicenciaGuard>
      <Layout>{children}</Layout>
    </LicenciaGuard>
  );
}

// Solo el superadmin (dueño de SaturnPRO) accede al panel SaaS
function AdminRoute({ children }) {
  const { usuario } = useAuth();
  if (!usuario) return <Navigate to="/login" />;
  if (!usuario.is_superadmin) return <Navigate to="/dashboard" />;
  return children;
}

function AppRoutes() {
  const { usuario } = useAuth();
  return (
    <Routes>
      <Route path="/login" element={usuario ? <Navigate to={usuario.is_superadmin ? '/superadmin' : '/dashboard'} /> : <Login />} />
      <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
      <Route path="/clientes" element={<PrivateRoute><Clientes /></PrivateRoute>} />
      <Route path="/clientes/:id/estado-cuenta" element={<PrivateRoute><ClienteDetalle /></PrivateRoute>} />
      <Route path="/creditos" element={<PrivateRoute><Creditos /></PrivateRoute>} />
      <Route path="/rutas" element={<PrivateRoute><Rutas /></PrivateRoute>} />
      <Route path="/rutas/:id/guia" element={<PrivateRoute><GuiaRuta /></PrivateRoute>} />
      <Route path="/cobros" element={<PrivateRoute><Cobros /></PrivateRoute>} />
      <Route path="/reportes" element={<PrivateRoute><Reportes /></PrivateRoute>} />
      <Route path="/usuarios" element={<PrivateRoute soloAdmin><Usuarios /></PrivateRoute>} />
      <Route path="/configuracion" element={<PrivateRoute soloAdmin><Configuracion /></PrivateRoute>} />
      <Route path="/productos" element={<PrivateRoute><Productos /></PrivateRoute>} />
      <Route path="/ventas" element={<PrivateRoute><Ventas /></PrivateRoute>} />
      <Route path="/ventas/nueva" element={<PrivateRoute><NuevaVenta /></PrivateRoute>} />

      {/* Admin SaaS Panel */}
      <Route path="/superadmin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
        <Route index element={<AdminDashboard />} />
        <Route path="clientes" element={<AdminClientes />} />
        <Route path="clientes/:id" element={<AdminClienteDetalle />} />
        <Route path="planes" element={<AdminPlanes />} />
        <Route path="suscripciones" element={<AdminSuscripciones />} />
        <Route path="pagos" element={<AdminPagos />} />
        <Route path="crm" element={<AdminCRM />} />
        <Route path="reportes" element={<AdminReportes />} />
      </Route>

      <Route path="*" element={<Navigate to="/dashboard" />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <LicenciaProvider>
            <AppRoutes />
            <ToastContainer position="top-right" theme="dark" autoClose={3000} />
          </LicenciaProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}
